import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Flame, Thermometer } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { HotPlate } from '@/components/lab';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Adding Boiling Chips',
    content: 'Boiling chips (porous ceramic or stone pieces) provide nucleation sites for bubble formation during heating. This prevents "bumping" - sudden, violent boiling that can splash hot liquid.',
    whatHappens: 'The boiling chips sink to the bottom of the beaker. Their rough, porous surface will allow bubbles to form smoothly during heating.',
    observe: [
      'Small white/gray ceramic chips',
      'Chips settle at the bottom',
      'Do not add chips to already boiling liquid',
      'Use 2-3 chips for this volume'
    ]
  },
  2: {
    title: 'Thermal Dehydration of Cu(OH)₂',
    content: 'This is a decomposition (dehydration) reaction:\n\nCu(OH)₂(s) -> CuO(s) + H₂O(g)\n\nHeat drives off water molecules, converting blue copper(II) hydroxide to black copper(II) oxide. The color change from blue to black is dramatic and characteristic.',
    whatHappens: 'As the solution is heated to boiling, the blue Cu(OH)₂ gradually darkens through green-brown intermediates to form black CuO. Water evaporates as steam.',
    observe: [
      'Blue precipitate gradually darkens',
      'Color progression: blue -> green -> brown -> black',
      'Steam rises as water is driven off',
      'Black CuO settles at the bottom',
      'Supernatant becomes clearer'
    ]
  }
};

// Steam animation component
function SteamAnimation({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <g>
      {[...Array(5)].map((_, i) => (
        <ellipse
          key={i}
          cx={50 + i * 15}
          cy={20}
          rx={4 + i}
          ry={3}
          fill={copperCycleColors.gases.steam.color}
          opacity={copperCycleColors.gases.steam.opacity}
        >
          <animate
            attributeName="cy"
            values="20;-10;20"
            dur={`${1.5 + i * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.3}s`}
          />
          <animate
            attributeName="opacity"
            values="0.4;0.1;0"
            dur={`${1.5 + i * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.3}s`}
          />
          <animate
            attributeName="rx"
            values="4;8;12"
            dur={`${1.5 + i * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.3}s`}
          />
        </ellipse>
      ))}
    </g>
  );
}

export default function Part3Dehydration() {
  const [boilingChipsAdded, setBoilingChipsAdded] = useState(false);
  const [heating, setHeating] = useState(false);
  const [heatingProgress, setHeatingProgress] = useState(0);
  const [dehydrationComplete, setDehydrationComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [temperature, setTemperature] = useState(25);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [boilingChipsAdded, dehydrationComplete].filter(Boolean).length;

  const handleAddChips = useCallback(() => {
    if (!boilingChipsAdded) {
      setBoilingChipsAdded(true);
      setCurrentStep(2);
    }
  }, [boilingChipsAdded]);

  const handleStartHeating = useCallback(() => {
    if (boilingChipsAdded && !heating && !dehydrationComplete) {
      setHeating(true);

      // Simulate heating with color change
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.025;
        setHeatingProgress(progress);
        setTemperature(Math.min(100, 25 + progress * 75));
        if (progress >= 1) {
          clearInterval(interval);
          setHeating(false);
          setDehydrationComplete(true);
        }
      }, 150);
    }
  }, [boilingChipsAdded, heating, dehydrationComplete]);

  // Color transition from blue Cu(OH)₂ to black CuO
  const getSolidColor = () => {
    if (dehydrationComplete) return copperCycleColors.cuOSolid;
    if (heatingProgress > 0.75) return copperCycleColors.cuOH2Heating3;
    if (heatingProgress > 0.5) return copperCycleColors.cuOH2Heating2;
    if (heatingProgress > 0.25) return copperCycleColors.cuOH2Heating1;
    return copperCycleColors.cuOH2Precipitate;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part V: Cu(OH)₂ Dehydration</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Add Boiling Chips', hint: 'Prevent bumping during heating', done: boilingChipsAdded },
            { id: 2, title: 'Heat to Boiling', hint: 'Watch the blue-to-black transition', done: dehydrationComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Temperature display */}
        <div className="temp-display">
          <Thermometer size={16} />
          <span>{temperature.toFixed(0)}C</span>
          <div className="temp-bar">
            <div className="temp-fill" style={{ width: `${((temperature - 25) / 75) * 100}%` }} />
          </div>
        </div>

        {/* Color transition indicator */}
        <div className="color-transition">
          <span className="label">Color Transition:</span>
          <div className="color-strip">
            <div className="color-swatch" style={{ background: copperCycleColors.cuOH2Precipitate }} />
            <div className="arrow">→</div>
            <div className="color-swatch" style={{ background: copperCycleColors.cuOH2Heating1 }} />
            <div className="arrow">→</div>
            <div className="color-swatch" style={{ background: copperCycleColors.cuOH2Heating2 }} />
            <div className="arrow">→</div>
            <div className="color-swatch current" style={{ background: getSolidColor() }} />
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Dehydration of Cu(OH)₂"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['Beaker with Cu(OH)₂', 'Hot plate', 'Boiling chips']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Boiling Chips */}
            <div
              className={`equipment-station ${currentStep === 1 && !boilingChipsAdded ? 'clickable' : ''}`}
              onClick={handleAddChips}
              style={{ cursor: currentStep === 1 && !boilingChipsAdded ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 60" width={45} height={55}>
                <rect x="10" y="15" width="30" height="35" fill="#555" stroke="#777" strokeWidth="1" rx="3" />
                {[...Array(6)].map((_, i) => (
                  <circle
                    key={i}
                    cx={18 + (i % 2) * 14}
                    cy={25 + Math.floor(i / 2) * 10}
                    r={3}
                    fill={copperCycleColors.boilingChips}
                  />
                ))}
                <text x="25" y="58" fontSize="7" fill="#E0E0E0" textAnchor="middle">Chips</text>
              </svg>
              <span className="equipment-label">Boiling Chips</span>
            </div>

            {/* Beaker on Hot Plate */}
            <div
              className={`equipment-station ${currentStep === 2 && !dehydrationComplete ? 'clickable' : ''}`}
              onClick={handleStartHeating}
              style={{ cursor: currentStep === 2 && boilingChipsAdded && !dehydrationComplete ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 160 220" width={180} height={250}>
                <defs>
                  <linearGradient id={`beaker-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(220,230,240,0.3)" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="rgba(200,210,220,0.4)" stopOpacity="0.7" />
                  </linearGradient>
                </defs>

                {/* Steam when heating */}
                <SteamAnimation active={heating} />

                {/* Beaker */}
                <rect x="35" y="35" width="90" height="110" fill={`url(#beaker-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />

                {/* Supernatant liquid */}
                <rect x="38" y="55" width="84" height="88" fill={`url(#liquid-${id})`} />

                {/* Solid precipitate/oxide */}
                <ellipse
                  cx="80"
                  cy="135"
                  rx="38"
                  ry="10"
                  fill={getSolidColor()}
                >
                  <animate
                    attributeName="fill"
                    values={heating ?
                      `${copperCycleColors.cuOH2Precipitate};${copperCycleColors.cuOH2Heating1};${copperCycleColors.cuOH2Heating2};${copperCycleColors.cuOH2Heating3};${copperCycleColors.cuOSolid}` : undefined}
                    dur={heating ? "6s" : undefined}
                    fill="freeze"
                  />
                </ellipse>

                {/* Boiling chips */}
                {boilingChipsAdded && (
                  <g>
                    <circle cx="55" cy="130" r="2" fill={copperCycleColors.boilingChips} />
                    <circle cx="95" cy="128" r="2" fill={copperCycleColors.boilingChips} />
                    <circle cx="75" cy="132" r="2" fill={copperCycleColors.boilingChips} />
                  </g>
                )}

                {/* Bubbles when boiling */}
                {heating && temperature > 90 && (
                  <g>
                    {[...Array(5)].map((_, i) => (
                      <circle
                        key={i}
                        cx={50 + i * 15}
                        cy={100}
                        r={2}
                        fill="rgba(255,255,255,0.5)"
                      >
                        <animate
                          attributeName="cy"
                          values="120;60;120"
                          dur={`${0.5 + i * 0.1}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.5;0;0.5"
                          dur={`${0.5 + i * 0.1}s`}
                          repeatCount="indefinite"
                        />
                      </circle>
                    ))}
                  </g>
                )}

                {/* Hot plate representation */}
                <rect x="20" y="150" width="120" height="50" fill="#3A3A3A" rx="5" />
                <rect x="30" y="155" width="100" height="25" fill="#2A2A2A" rx="3" />

                {/* Heat coils */}
                <g opacity={heating ? 1 : 0.3}>
                  <circle cx="80" cy="167" r="20" fill="none" stroke={heating ? "#FF4500" : "#4A4A4A"} strokeWidth="2">
                    {heating && (
                      <animate attributeName="stroke" values="#8B4513;#CD5C5C;#FF4500" dur="2s" repeatCount="indefinite" />
                    )}
                  </circle>
                  <circle cx="80" cy="167" r="12" fill="none" stroke={heating ? "#FF4500" : "#4A4A4A"} strokeWidth="2">
                    {heating && (
                      <animate attributeName="stroke" values="#CD5C5C;#FF4500;#FF6347" dur="2s" repeatCount="indefinite" />
                    )}
                  </circle>
                </g>

                {/* Power LED */}
                <circle cx="130" cy="185" r="3" fill={heating ? "#00FF00" : "#333"} />

                <text x="80" y="215" fontSize="10" fill="#E0E0E0" textAnchor="middle">Hot Plate</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !boilingChipsAdded && (
              <div className="status-message info">
                <Flame size={20} />
                Add boiling chips to prevent bumping
              </div>
            )}
            {currentStep === 2 && boilingChipsAdded && !heating && !dehydrationComplete && (
              <div className="status-message info">
                <Flame size={20} />
                Click to start heating - watch the color change!
              </div>
            )}
            {heating && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                Heating... {(heatingProgress * 100).toFixed(0)}% - Blue → Black transition
              </div>
            )}
            {dehydrationComplete && (
              <div className="status-message success">
                <Check size={20} />
                Black CuO formed! Water has been driven off.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Dehydration Reaction</h4>
            <div className="equation">
              Cu(OH)₂ → CuO + H₂O
            </div>
            <p>
              When heated, copper(II) hydroxide loses water (dehydrates) to form black copper(II) oxide.
              The dramatic color change from bright blue to black indicates the chemical transformation.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .temp-display {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 12px;
            padding: 8px 12px;
            background: var(--bg-elevated);
            border-radius: 6px;
            color: #FF5A36;
          }

          .temp-bar {
            flex: 1;
            height: 6px;
            background: #333;
            border-radius: 3px;
            overflow: hidden;
          }

          .temp-fill {
            height: 100%;
            background: linear-gradient(90deg, #FFFF00, #FF5A36, #FF0000);
            transition: width 0.2s;
          }

          .color-transition {
            margin-top: 12px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .color-transition .label {
            font-size: 11px;
            color: var(--text-secondary);
            display: block;
            margin-bottom: 8px;
          }

          .color-strip {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .color-swatch {
            width: 20px;
            height: 20px;
            border-radius: 3px;
            border: 1px solid rgba(255,255,255,0.2);
          }

          .color-swatch.current {
            width: 28px;
            height: 28px;
            border: 2px solid var(--secondary);
          }

          .arrow {
            color: var(--text-secondary);
            font-size: 10px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(255, 90, 54, 0.2);
            color: #FF5A36;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: #1A1A1A;
            background: linear-gradient(90deg, ${copperCycleColors.cuOH2Precipitate}, ${copperCycleColors.cuOSolid});
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: bold;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
